<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="main">
    <div class="left-container">
      <div class="filter-table-controls">
        <div class="filter-summary-container">
          <span>{{ filterSummary }}</span>
        </div>
        <ButtonFilter buttonText="Change Filter" filterPosition="right" :filterWidth="500" :isOpen.sync="filterIsOpen">
          <div class="filter-content">
            <BoxContainer
              title="PR Date"
              :isCheckBox="true"
              :isChecked.sync="prDateFilterIsChecked"
              @checkBoxClicked="dateFilterCheckBoxClicked"
              style="display: flex; flex-direction: row; gap: 8px"
            >
              <DateTimePicker
                placeholder="Start Date"
                :isRequired="true"
                :value.sync="startPRDateValue"
                :errorMessage.sync="startPRDateValueError"
                :isDisabled="!prDateFilterIsChecked"
              />
              <DateTimePicker
                placeholder="End Date"
                :value.sync="endPRDateValue"
                :isDisabled="!prDateFilterIsChecked"
              />
            </BoxContainer>
            <BoxContainer
              title="PR No"
              :isCheckBox="true"
              :isChecked.sync="prNoFilterIsChecked"
              style="display: flex; flex-direction: row; gap: 8px"
              @checkBoxClicked="prNoFilterCheckBoxClicked"
            >
              <Dropdown
                placeholder="Start PR No"
                :isRequired="true"
                :selectedObject.sync="prNoStartValue"
                :errorMessage.sync="prNoStartValueError"
                :isDisabled="!prNoFilterIsChecked"
                itemKey="PrNo"
                :items="prNoList"
              />
              <Dropdown
                placeholder="Start PR No"
                :selectedObject.sync="prNoEndValue"
                :isDisabled="!prNoFilterIsChecked"
                itemKey="PrNo"
                :items="prNoList"
              />
            </BoxContainer>
            <div class="refresh-button">
              <button @click="refreshFilter">Refresh</button>
            </div>
          </div>
        </ButtonFilter>
      </div>
      <TableBox
        :tableData="prListFiltered"
        :selectedRow.sync="prTableSelectedRows"
        title="Purchase Requisition"
        :loading="prTableLoading"
        :maxSelection="-1"
        :showSelectAll="true"
        style="min-height: 0"
        :dataType="prListDataType"
      />
      <div style="display: flex; justify-content: end; width: 100%; margin-top: 10px">
        <Button style="width: fit-content" @click="previewClicked">Preview Purchase Requisition</Button></div
      >
    </div>
    <div class="right-container"
      ><DesignerV3
        v-if="showDesignerV3"
        :reportFiles="designerV3ReportFiles"
        :reportData="designerV3ReportData"
        :sharedData="designerV3SharedData"
        :canShare="true"
        :shareBehaviour="designerV3ShareBehaviour"
        :canClose="false"
        ref="designerV3"
        @shareClicked="designerV3ShareClicked"
      />
      <div class="user-instructions" v-else>
        <span
          >Select some Purchase Requisition and click on <br /><strong>Preview Purchase Requisition</strong> to get
          started</span
        >
      </div></div
    >

    <PopupV2
      class="email-popup"
      :show="showEmailPopup"
      icon="https://dsmlib.logistics.systems/Icons/BlackMail.png"
      :buttons="emailPopupButtons"
      title="Email Work Order"
      width="60%"
      @buttonClicked="emailPopupButtonClicked"
    >
      <div class="email-popup-content-container">
        <div class="mail-inputs-container">
          <div class="email-input-container">
            <EmailInput :mails.sync="emailPopupToMails" title="To" />
          </div>
          <div class="email-input-container">
            <EmailInput :mails.sync="emailPopupCCMails" title="CC" />
          </div>
          <div class="email-input-container">
            <EmailInput :mails.sync="emailPopupBCCMails" title="BCC" />
          </div>
        </div>
        <div class="email-content-inputs-container">
          <Input :inputValue.sync="emailPopupSubjectInputValue" placeholder="Subject" validator="text,100" />
          <RemarksBox title="Message" :text.sync="emailPopupMessageText" :maxLength="1000" />
        </div>
      </div>
    </PopupV2>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {
    filterSummary() {
      if (this.filterActive && this.startPRDateValueSaved && this.endPRDateValueSaved) {
        return `PR Date: ${this.startPRDateValueSaved.formatString('yyyy-MM-dd HH:mm')} to ${this.endPRDateValueSaved.formatString('yyyy-MM-dd HH:mm')}`;
      } else if (this.filterActive && this.prNoStartValueSaved && this.prNoEndValueSaved) {
        return `PR No: ${this.prNoStartValueSaved} to ${this.prNoEndValueSaved}`;
      }
      return 'No filter applied!';
    },

    prListFiltered() {
      const relabel = this.prList.map((item, index) => {
        const {
          PrNo,
          PrDate,
          RequestBy,
          Position,
          Pesanan,
          Expenses,
          EstCost,
          Period,
          UserId,
          DateUpdate,
          BranchCheck,
          BranchApproval,
          HQApproval,
        } = item || {};
        return {
          '#': index + 1,
          'PR #': PrNo,
          Date: PrDate,
          'Requested By': `${RequestBy} ${Position ? `(${Position})` : ''}`,
          'PO/WO': Pesanan == '1' ? 'Work Order' : 'Purchase Requisition',
          'Expenses Type': Expenses == '1' ? 'OPEX' : 'CAPEX',
          'Estimated Cost': EstCost,
          Period: Period,
          'Approval From HQ': HQApproval == '1' ? 'Approved ✅' : 'Pending',
          'Checking From Branch': BranchCheck == '1' ? 'Checked ✅' : 'Pending',
          'Approval From Branch': BranchApproval == '1' ? 'Approved ✅' : 'Pending',
          'Updated By': UserId,
          'Date Updated': DateUpdate,
        };
      });
      return relabel;
    },
  },
  data: function () {
    return {
      filterIsOpen: false,

      filterActive: false,

      filterPRNo: null,

      prDateFilterIsChecked: false,
      startPRDateValue: null,
      endPRDateValue: null,
      startPRDateValueSaved: null,
      endPRDateValueSaved: null,

      startPRDateValueError: '',

      prNoList: [],

      prNoFilterIsChecked: false,
      prNoStartValue: null,
      prNoEndValue: null,
      prNoStartValueSaved: '',
      prNoEndValueSaved: '',

      prNoStartValueError: '',

      prList: [],
      prTableSelectedRows: [],
      prTableLoading: true,

      prListDataType: {
        '#': 'number',
        'PR #': 'string',
        Date: 'date',
        'Requested By': 'string',
        'PO/WO': 'string',
        'Expenses Type': 'string',
        'Estimated Cost': 'currency',
        Period: 'string',
        'Approval From HQ': 'string',
        'Checking From Branch': 'string',
        'Approval From Branch': 'string',
        'Updated By': 'string',
        'Updated Date': 'datetime',
      },

      showDesignerV3: false,
      designerV3ReportFiles: [],
      designerV3ReportData: [],
      designerV3SharedData: [],
      designerV3ShareBehaviour: {
        behaviour: 'uploadAndEmit',
        options: {
          fileName: '',
          folder: '',
        },
      },

      showEmailPopup: false,
      emailPopupButtons: [{ text: 'Send Email' }, { text: 'Cancel', color: 'lightgray' }],
      emailPopupToMails: [],
      emailPopupCCMails: [],
      emailPopupBCCMails: [],
      emailPopupSubjectInputValue: '',
      emailPopupMessageText: '',

      supplierList: [],

      localCurrency: 'RM',
    };
  },
  props: {},
  methods: {
    async getPRNoList() {
      const result = await this.$apiCallV2('/PurchaseRequisition/GetPRNoList', {});
      this.prNoList = result;
    },

    async getPR() {
      this.prTableLoading = true;
      const result = await this.$apiCallV2('/PurchaseRequisition/GetPRList', {
        startdate: this.prDateFilterIsChecked ? (this.startPRDateValueSaved.formatString('yyyy-MM-dd') ?? null) : null,
        enddate: this.prDateFilterIsChecked ? (this.endPRDateValueSaved.formatString('yyyy-MM-dd') ?? null) : null,
        startprno: this.prNoFilterIsChecked ? (this.prNoStartValueSaved ?? null) : null,
        endprno: this.prNoFilterIsChecked ? (this.prNoEndValueSaved ?? null) : null,
      });
      this.prList = [];
      this.$nextTick(() => {
        this.prList = result;
      });
      this.prTableLoading = false;
      this.prTableSelectedRows = [];
    },

    refreshFilter() {
      if (!this.prDateFilterIsChecked && !this.prNoFilterIsChecked) {
        this.filterActive = false;
        this.filterIsOpen = false;
        return;
      }
      this.startPRDateValueError = '';
      this.prNoStartValueError = '';

      if (this.prDateFilterIsChecked && !this.startPRDateValue) {
        this.startPRDateValueError = 'Start Date is required';
        return;
      }

      if (this.getPRNoFilterIsChecked && !this.prNoStartValue) {
        this.prNoStartValueError = 'Start PR No is required';
        return;
      }

      if (this.prDateFilterIsChecked) {
        this.startPRDateValueSaved = this.startPRDateValue;
        if (!this.endPRDateValue) this.endPRDateValue = new Date();
        this.endPRDateValueSaved = this.endPRDateValue;
      }

      if (this.prNoFilterIsChecked) {
        this.prNoStartValueSaved = this.prNoStartValue.PrNo;
        if (!this.prNoEndValue) this.prNoEndValue = this.prNoStartValue;
        this.prNoEndValueSaved = this.prNoEndValue.PrNo;
      }

      this.filterActive = true;
      this.filterIsOpen = false;
      this.prTableSelectedRows = [];
      this.getPR();
    },

    async previewClicked() {
      this.$fullscreenLoading('Getting Purchase Requisition...');

      const prlist = this.prTableSelectedRows.map((item) => item['PR #']).join(',');

      const result = await this.$apiCallV2('/PurchaseRequisition/PrintPurchaseRequisition', { prlist });

      this.$fullscreenLoading();

      const { CompanyInfo, PR: resultPR, Supplier: supplier } = result.PrintPR || {};
      const PR = this.convertToArray(resultPR);
      const prItems = this.convertToArray(PR[0].PRDetails ?? []);
      const suppliersFromItemList = [...new Set(prItems.map((item) => item.Supplier))];

      const Supplier = this.convertToArray(supplier)
        .slice()
        .sort((a, b) => suppliersFromItemList.indexOf(a.Spp_Code) - suppliersFromItemList.indexOf(b.Spp_Code));

      const { addr1, addr2, addr3, addr4 } = CompanyInfo;

      const designerFiles = {
        general: {
          companyName: addr1 ?? '',
          companyAddress: [addr2, addr3, addr4].filter(Boolean).join('\n'),
          localCurr: this.localCurrency,
        },
        purchaseRequisition: PR?.map((pr) => {
          const { PRNo, PRDate, PRDetails: prdetails, Period, Justification, PIC1, PIC3, PIC6 } = pr;

          const PRDetails = this.convertToArray(prdetails);

          const prItems = PRDetails.map((prodItem, index) => {
            const {
              Product,
              Name,
              Supplier: SupplierProdItem,
              Qty,
              UnitPrice,
              Taxable_AmountGST2: TaxableAmount,
            } = prodItem;
            const totalAmount = Number(Qty) * Number(UnitPrice) + Number(TaxableAmount ?? '0');
            return {
              itemIndex: index + 1,
              prodCode: Product,
              prodName: Name,
              supplier: SupplierProdItem,
              prodQty: Qty,
              unitPrice: this.$currencyFormat(UnitPrice),
              taxAmt: this.$currencyFormat(TaxableAmount ?? '0'),
              totalPrice: this.$currencyFormat(totalAmount),
            };
          });

          const SupplierSet = [...new Set(prItems.map((item) => item.supplier))];

          const supplierFindSingle = Supplier.find((s) => s.Spp_Code == SupplierSet[0]) || {};

          const {
            Spp_Code,
            Spp_Contact,
            Spp_Name,
            Spp_Addr1,
            Spp_Addr2,
            Spp_Addr3,
            Spp_Addr4,
            Spp_TelNo,
            Spp_Fax,
            Spp_Email,
          } = supplierFindSingle;

          const total = prItems.reduce((sum, item) => sum + Number(item.totalPrice.replace(/,/g, '')), 0);
          return {
            prNo: PRNo,
            date: new Date(PRDate).formatString('dd/MM/yyyy'),
            pageNo: '{{jsreportPageNum}} of {{jsreportTotalPage}}',
            prodItem: prItems,

            sppSingle: SupplierSet.length == 1,

            supplierContact: Spp_Contact,
            supplierName: `${Spp_Name} (${Spp_Code})`,
            supplierAddress: [Spp_Addr1, Spp_Addr2, Spp_Addr3, Spp_Addr4].filter(Boolean).join('\n'),
            supplierPhone1: Spp_TelNo ? `Tel: ${Spp_TelNo}` : '',
            supplierPhone2: Spp_Fax ? `Fax: ${Spp_Fax}` : '',
            supplierEmail: Spp_Email ? `Email: ${Spp_Email}` : '',

            sppMultiple: SupplierSet.length > 1,
            supplierItem: SupplierSet.map((supplier) => {
              const supplierFind = Supplier.find((s) => s.Spp_Code == supplier);
              const {
                Spp_Code,
                Spp_Contact,
                Spp_Name,
                Spp_Addr1,
                Spp_Addr2,
                Spp_Addr3,
                Spp_Addr4,
                Spp_TelNo,
                Spp_Fax,
                Spp_Email,
              } = supplierFind || {};
              return {
                supplierContact: Spp_Contact,
                supplierName: `${Spp_Name} (${Spp_Code})`,
                supplierAddress: [Spp_Addr1, Spp_Addr2, Spp_Addr3, Spp_Addr4].filter(Boolean).join('\n'),
                supplierPhone1: Spp_TelNo ? `Tel: ${Spp_TelNo}` : '',
                supplierPhone2: Spp_Fax ? `Fax: ${Spp_Fax}` : '',
                supplierEmail: Spp_Email ? `Email: ${Spp_Email}` : '',
                supplierProdItem: prItems
                  .filter((item) => item.supplier == Spp_Code)
                  .map((item, index) => {
                    return {
                      ...item,
                      itemIndex: index + 1,
                    };
                  }),
                localCurr: this.localCurrency,
              };
            }),

            totalAfterTax: this.$currencyFormat(total),
            period: Period ?? '',
            justification: Justification ?? '',
            PIC1: PIC1 ?? '',
            PIC3: PIC3 ?? '',
            PIC6: PIC6 ?? '',
          };
        }),
      };

      this.designerV3ReportFiles = Array.from(
        { length: designerFiles.purchaseRequisition.length },
        () => 'PurchaseRequisition.json',
      );
      this.designerV3ReportData = designerFiles.purchaseRequisition;
      this.designerV3SharedData = designerFiles.general;
      this.showDesignerV3 = true;
    },

    designerV3ShareClicked(shareLink) {
      if (this.designerV3ReportData[0].email) {
        this.emailPopupToMails = this.designerV3ReportData[0].email.split(';').map((email) => ({
          email: email.trim(),
          valid: true,
        }));
      } else {
        this.emailPopupToMails = [];
      }
      this.emailPopupCCMails = [];
      this.emailPopupBCCMails = [];

      this.emailPopupSubjectInputValue = 'Purchase Requisition';
      this.emailPopupMessageText = `Purchase Requisition: ${shareLink}\n\n`;

      this.showEmailPopup = true;
    },

    async emailPopupButtonClicked(buttonText) {
      if (buttonText === 'Cancel') {
        this.showEmailPopup = false;
        return;
      }

      if (this.emailPopupToMails.filter((mail) => mail.valid).length === 0) {
        this.$showAlert('error', 'No Valid Email', "Please enter at least one valid 'To' email address", [
          { text: 'OK' },
        ]);
        return;
      }

      if (!this.emailPopupSubjectInputValue.trim()) {
        const proceedWithoutSubject = await this.$showAlert(
          'warning',
          'No Subject',
          'No subject was entered\nProceed to email without a subject?',
          [{ text: 'Proceed' }, { text: 'Cancel', color: 'lightgray' }],
        );

        if (proceedWithoutSubject === 'Cancel') return;
      }

      const requestBody = {
        emaillist: [
          {
            doctype: 'PURCHASEREQUISITION',
            docno: this.designerV3ReportData[0]['prNo'],
            docnofieldname: '',
            recipient: this.emailPopupToMails.map((mail) => mail.email).join(';'),
            cc: this.emailPopupCCMails.map((mail) => mail.email).join(';'),
            bcc: this.emailPopupBCCMails.map((mail) => mail.email).join(';'),
            subject: this.emailPopupSubjectInputValue.trim(),
            message: this.emailPopupMessageText,
          },
        ],
      };

      this.$fullscreenLoading('Sending Email');
      const sendEmailResponse = await this.$apiCallV2('/PurchaseRequisition/SendEmail', requestBody);
      this.$fullscreenLoading();

      if (sendEmailResponse?.trim?.()?.endsWith?.('queue to send.')) {
        this.$showAlert('success', 'Email successfully sent', 'Your email has been successfully sent', [
          { text: 'OK' },
        ]);
        this.showEmailPopup = false;
      } else {
        this.$showAlert('error', 'Email not sent', 'Your email could not be sent. Please try again.', [{ text: 'OK' }]);
      }
    },

    convertToArray(item) {
      if (!item) return [];
      return Array.isArray(item) ? item : [item];
    },

    async getSupplierList() {
      this.supplierList = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'SUPPLIER' });
    },

    async getLocalCurrency() {
      const companyDetails = await this.$apiCallV2('/Master/GetDetailsMaster', {
        masterfile: 'COMPANY',
        module: 'MASTER',
        recordid: this.$store.state.loginDetails.CoCode,
      });
      const currString = companyDetails.Company.CompanyLocalCur;
      this.localCurrency = currString ?? 'RM';
    },

    dateFilterCheckBoxClicked() {
      if (this.prDateFilterIsChecked) this.prNoFilterIsChecked = false;
      else this.prNoFilterIsChecked = true;
    },
    prNoFilterCheckBoxClicked() {
      if (this.prNoFilterIsChecked) this.prDateFilterIsChecked = false;
      else this.prDateFilterIsChecked = true;
    },
  },
  async mounted() {
    this.$fullscreenLoading('Getting Purchase Requisition...');
    await Promise.all([await this.getSupplierList(), await this.getPRNoList(), await this.getLocalCurrency()]);
    this.$fullscreenLoading();

    const query = this.$route.query.prno?.trim() ?? null;
    if (query) {
      this.filterPRNo = query;
      this.prNoStartValue = this.prNoList.find((pr) => pr.PrNo == query);
      this.prNoEndValue = this.prNoStartValue;
      this.prNoStartValueSaved = query;
      this.prNoEndValueSaved = query;

      this.prNoFilterIsChecked = true;

      await this.getPR();

      this.$nextTick(() => {
        this.prTableSelectedRows = [{ 'PR #': query, _rowid: 'row0' }];
      });
      this.filterActive = true;
    }
  },
  watch: {},
};
</script>
<style lang="scss" scoped>
@import '@/css/all.scss';
.main {
  padding: 10px 45px;
  display: flex;
  flex-direction: row;
  font-size: 12px;
  > .left-container {
    height: 100%;
    width: 50%;
    padding: 5px;
    border-radius: 5px;
    overflow: hidden;
    background-color: white;
    display: flex;
    flex-direction: column;
  }

  > .right-container {
    height: 100%;
    width: 50%;
    margin-left: 5px;
    border-radius: 5px;
    overflow: hidden;
    background-color: white;
    display: flex;
    flex-direction: column;

    .user-instructions {
      font-size: 1.25rem;
      padding: 3rem;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
    }
  }

  .filter-table-controls {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    margin-bottom: 5px;

    > .filter-summary-container {
      margin-right: 5px;
      align-content: center;
      text-align: end;
    }

    .filter-content {
      text-align: left;
      cursor: auto;

      .refresh-button {
        margin-top: 10px;
        display: flex;
        justify-content: flex-end;
      }
    }

    > .select-deselect-all-button {
      margin-left: 5px;
    }
  }
  .email-popup {
    .email-popup-content-container {
      display: flex;
      flex-direction: row;

      > .mail-inputs-container {
        margin-right: 5px;
        display: flex;
        flex-direction: column;
        flex: 1;

        > .email-input-container {
          margin-bottom: 5px;
        }
      }

      > .email-content-inputs-container {
        margin-left: 5px;
        display: flex;
        flex-direction: column;
        flex: 2;
      }
    }
  }
}
</style>
